'use client';

import { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useGetJobDetail, useSearchJobs } from '@/features/jobs';
import { PublicJobListDTO } from '@/shared/job/job.public.dto';
import {
  buildJobSearchParams,
  isRankedJobType,
  shouldShowRankNumber,
  unwrapJobList,
} from '@/features/jobs/lib/job-list.utils';

export function useJobResults(isNearby = false) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const currentPage = Number(searchParams.get('page')) || 1;
  const type = searchParams.get('type');
  const isRankedType = isRankedJobType(type);
  const showRankNumber = shouldShowRankNumber(type);

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedJob, setSelectedJob] = useState<PublicJobListDTO | null>(null);

  const jobSearchParams = useMemo(
    () => buildJobSearchParams(searchParams, { currentPage, isNearby }),
    [searchParams, currentPage, isNearby]
  );

  const {
    data: jobsData,
    isLoading: isListLoading,
    guestLimitMessage: searchLimitMessage,
  } = useSearchJobs(jobSearchParams);
  const { jobs, totalPages } = unwrapJobList(jobsData);

  useEffect(() => {
    if (jobs.length === 0) {
      setSelectedJob(null);
      return;
    }
    setSelectedJob(jobs[0] ?? null);
  }, [jobs]);

  const { data: jobDetail, guestLimitMessage: viewLimitMessage } =
    useGetJobDetail(selectedJob?.code ?? '');

  const selectJob = (job: PublicJobListDTO) => {
    setSelectedJob(job);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const paginate = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('page', pageNumber.toString());
    const path = isNearby ? '/job/near-me' : '/job/list';
    router.push(`${path}?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const goToSearch = (query: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('q', query);
    router.push(`/job/list?${params.toString()}`);
  };

  return {
    currentPage,
    isRankedType,
    showRankNumber,
    searchQuery,
    setSearchQuery,
    selectedJob,
    selectJob,
    jobs,
    totalPages,
    isListLoading,
    searchLimitMessage,
    viewLimitMessage,
    jobDetail,
    hasSearchParams: searchParams.toString().length > 0,
    paginate,
    goToSearch,
  };
}
